import classNames from "classnames"
import {kebabCase, startCase} from "lodash"
import moment from "moment"
import numeral from "numeral"
import React, {ReactElement} from "react"
import {useNavigate} from "react-router-dom"
import {Bill, findVendor, getBillDeductionDate, getVendorName} from "../../resources/bills"
import {Resource} from "../../resources/common"
import {Customer, getCustomerName} from "../../resources/customer"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import IdLink from "../IdLink/IdLink"
import {OrgName} from "../OrgName/OrgName"
import {AllowedBillsColumns, BillsColumns} from "./Bills"
import {useIsManagedSolutionsBankingUser} from "../../services/auth"

interface BillRowProps {
	bill: Bill
	include?: Resource[]
	includedColumns: AllowedBillsColumns[]
	onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void
}

export function BillRow({bill, include, includedColumns, onClick}: BillRowProps) {
	const navigate = useNavigate()
	const isManagedSolutionsBankingUser = useIsManagedSolutionsBankingUser()
	const id = bill.id
	const org = bill.relationships.org?.data.id
	const customerId = bill.relationships.customer?.data.id
	const vendorId = bill.relationships.vendor?.data.id
	const status = bill.attributes.status
	const total = bill.attributes.total
	const invoiceNumber = bill.attributes.invoiceNumber
	const dueDate = bill.attributes.dueDate
	const date = bill.attributes.date
	const createdAt = bill.attributes.createdAt
	const deductionDate = getBillDeductionDate(bill.attributes.paymentMethod)

	const customer =
		customerId && include
			? include.find(
					(r) => (r.type == "individualCustomer" || r.type == "businessCustomer") && r.id == customerId.toString()
			  )
			: undefined

	let customerName = customerId ?? ""

	if (customer) {
		customerName = getCustomerName(customer as Customer)
	}

	const vendor = vendorId && include ? findVendor(vendorId, include) : undefined
	const vendorName = vendor ? getVendorName(vendor) : vendorId ?? ""

	const contentColumns: Partial<Record<AllowedBillsColumns, ReactElement>> = {}

	if (includedColumns.includes(BillsColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell className="data-table-id-cell">
				<IdLink className="data-table-id" href={`/bills/${id}`}>
					{id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(BillsColumns.org) && org) {
		contentColumns["Org"] = <OrgName orgId={org.toString()} included={include} />
	}

	if (includedColumns.includes(BillsColumns.invoiceNumber)) {
		contentColumns["Invoice #"] = <DataTableCell>{invoiceNumber}</DataTableCell>
	}

	if (includedColumns.includes(BillsColumns.customer)) {
		contentColumns["Customer"] = (
			<DataTableCell>
				{customer ? (
					<a
						className="link"
						onClick={(e) => {
							e.preventDefault()
							e.stopPropagation()
							navigate(
								isManagedSolutionsBankingUser
									? `/managed-solutions/customers/${customerId}`
									: `/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`
							)
						}}
					>
						{customerName}
					</a>
				) : (
					customerId
				)}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(BillsColumns.vendor)) {
		contentColumns["Vendor"] = <DataTableCell>{vendorName}</DataTableCell>
	}

	if (includedColumns.includes(BillsColumns.amount)) {
		contentColumns["Amount"] = (
			<DataTableCell>
				<span className="payments-amount">{numeral(total / 100).format("$0,0.00")}</span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(BillsColumns.dueDate)) {
		contentColumns["Due Date"] = <DataTableCell>{moment(dueDate).format("L")}</DataTableCell>
	}

	if (includedColumns.includes(BillsColumns.deductionDate)) {
		contentColumns["Deduction Date"] = (
			<DataTableCell>{deductionDate ? moment(deductionDate).format("L") : "--"}</DataTableCell>
		)
	}

	if (includedColumns.includes(BillsColumns.date)) {
		contentColumns["Date"] = <DataTableCell>{moment(date).format("L")}</DataTableCell>
	}

	if (includedColumns.includes(BillsColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell>{createdAt ? moment(createdAt).format("L LT") : ""}</DataTableCell>
	}

	if (includedColumns.includes(BillsColumns.status)) {
		contentColumns["Status"] = (
			<DataTableCell>
				<div className={classNames("bills-status", kebabCase(status))}>{startCase(status)}</div>
			</DataTableCell>
		)
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return <DataTableRow onClick={(e) => onClick && onClick(e)}>{content}</DataTableRow>
}
